import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLazyQuery } from '@/apollo';
import { 
  X, 
  CheckCircle, 
  AlertTriangle,
  FileText,
  Database
} from 'lucide-react';
import { parseCSV } from '@pkg/csv-parse';
import { 
  CHECK_INSURANCE_EXISTS,
  CHECK_LOCATION, 
  CHECK_CONDITION, 
  CHECK_CUSTOMER,
  CHECK_APPOINTMENT_DETAIL, 
  CHECK_PROVIDER 
} from '../../graphql/imports';

// Inject custom scrollbar styles
if (typeof document !== 'undefined') {
  const styleId = 'custom-scrollbar-styles-preview';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .custom-scrollbar::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      
      .custom-scrollbar::-webkit-scrollbar-track {
        background: var(--color-bg-primary);
        border-radius: 4px;
      }
      
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: var(--color-border);
        border-radius: 4px;
        transition: background 0.2s;
      }
      
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: var(--color-text-muted);
      }
      
      /* Firefox scrollbar */
      .custom-scrollbar {
        scrollbar-width: thin;
        scrollbar-color: var(--color-border) var(--color-bg-primary);
      }
    `;
    document.head.appendChild(style);
  }
}

interface CSVPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  csvData: string;
  importType: 'insurances' | 'locations' | 'conditions' | 'customers' | 'appointment-details' | 'providers';
  onConfirm: () => void;
  isImporting?: boolean;
}

interface PreviewRow {
  data: string[];
  isExisting: boolean;
  existingRecord?: any;
}

export const CSVPreviewModal: React.FC<CSVPreviewModalProps> = ({
  isOpen,
  onClose,
  csvData,
  importType,
  onConfirm,
  isImporting = false
}) => {
  const [previewData, setPreviewData] = useState<PreviewRow[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [checkInsuranceExists] = useLazyQuery(CHECK_INSURANCE_EXISTS);
  const [checkLocation] = useLazyQuery(CHECK_LOCATION);
  const [checkCondition] = useLazyQuery(CHECK_CONDITION);
  const [checkCustomer] = useLazyQuery(CHECK_CUSTOMER);
  const [checkAppointmentDetail] = useLazyQuery(CHECK_APPOINTMENT_DETAIL);
  const [checkProvider] = useLazyQuery(CHECK_PROVIDER);

  useEffect(() => {
    if (isOpen && csvData) {
      parseAndPreviewData();
    }
  }, [isOpen, csvData, importType]);

  const checkExistingRecords = async (rows: string[][], headers: string[]): Promise<PreviewRow[]> => {
    const previewRows: PreviewRow[] = [];
    
    // Track seen items in current CSV to detect duplicates within the file
    const seenInCSV = new Map<string, number>();
    
    for (let i = 1; i < rows.length; i++) { // Skip header row
      const row = rows[i];
      if (!row) continue;
      
      let isExisting = false;
      let existingRecord = null;
      let duplicateInCSV = false;
      let duplicateRow = 0;

      try {
        switch (importType) {
          case 'insurances': {
            const normalizeHeaderKey = (h: string) => h.toLowerCase().replace(/[\s_-]/g, '');
            const nameIndex = (() => {
              const byExact = headers.findIndex((h) => {
                const n = normalizeHeaderKey(h);
                return n === 'name' || n === 'insurancename';
              });
              if (byExact !== -1) return byExact;
              return headers.findIndex(
                (h) => h.toLowerCase().includes('name') && !h.toLowerCase().includes('customer')
              );
            })();
            const customerIndex = (() => {
              const byExact = headers.findIndex((h) => {
                const n = normalizeHeaderKey(h);
                return n === 'customer' || n === 'customername';
              });
              if (byExact !== -1) return byExact;
              return headers.findIndex(
                (h) => h.toLowerCase().includes('customer') && !h.toLowerCase().includes('id')
              );
            })();
            if (nameIndex !== -1 && row[nameIndex]) {
              const trimmedName = (row[nameIndex] || '').trim();
              if (!trimmedName) break;
              const trimmedCustomerName =
                customerIndex !== -1 ? (row[customerIndex] || '').trim() || null : null;
              const insuranceKey = `${trimmedName.toLowerCase()}|${(trimmedCustomerName ?? '').toLowerCase()}`;
              if (seenInCSV.has(insuranceKey)) {
                duplicateInCSV = true;
                duplicateRow = seenInCSV.get(insuranceKey)!;
              } else {
                seenInCSV.set(insuranceKey, i + 1);
                const result = await checkInsuranceExists({
                  variables: { name: trimmedName, customerName: trimmedCustomerName ?? undefined },
                  fetchPolicy: 'network-only'
                });
                if (result.data?.checkInsuranceExists?.exists && result.data?.checkInsuranceExists?.insurance) {
                  isExisting = true;
                  existingRecord = result.data.checkInsuranceExists.insurance;
                }
              }
            }
            break;
          }

          case 'locations':
            const cityIndex = headers.findIndex(h => h.toLowerCase().includes('city'));
            const stateIndex = headers.findIndex(h => h.toLowerCase().includes('state'));
            const zipCodeIndex = headers.findIndex(h => h.toLowerCase().includes('zipcode') || h.toLowerCase().includes('zip'));
            const addressIndex = headers.findIndex(h => 
              h.toLowerCase().includes('address') || 
              h.toLowerCase().includes('physical address') || 
              h.toLowerCase().includes('street')
            );
            const locationCustomerIndex = headers.findIndex(h => 
              h.toLowerCase().includes('customer') && !h.toLowerCase().includes('id')
            );
            
            if (cityIndex !== -1 && stateIndex !== -1 && row[cityIndex] && row[stateIndex]) {
              const city = (row[cityIndex] || '').trim();
              const state = (row[stateIndex] || '').trim();
              const zipCode = zipCodeIndex !== -1 ? (row[zipCodeIndex] || '').trim() : '';
              const physicalAddress = addressIndex !== -1 ? (row[addressIndex] || '').trim() || null : null;
              const customerName = locationCustomerIndex !== -1 ? (row[locationCustomerIndex] || '').trim() || null : null;
              
              if (!city || !state) break;
              
              // Use Physical Address + City + State + ZipCode + Customer as the key for duplicate checking
              const normalizedPhysicalAddress = physicalAddress ? physicalAddress.toLowerCase() : '';
              const normalizedCustomerName = customerName ? customerName.toLowerCase() : '';
              const locationKey = `${normalizedPhysicalAddress}|${city.toLowerCase()}|${state.toLowerCase()}|${zipCode.toLowerCase()}|${normalizedCustomerName}`;
              
              // Check for duplicate in CSV
              if (seenInCSV.has(locationKey)) {
                duplicateInCSV = true;
                duplicateRow = seenInCSV.get(locationKey)!;
              } else {
                seenInCSV.set(locationKey, i + 1);
                const result = await checkLocation({ 
                  variables: { 
                    city: city,
                    state: state,
                    zipCode: zipCode || null,
                    physicalAddress: physicalAddress || null,
                    customerName: customerName || null
                  },
                  fetchPolicy: 'network-only'
                });
                if (result.data?.checkLocationExists?.exists && result.data?.checkLocationExists?.location) {
                  isExisting = true;
                  existingRecord = result.data.checkLocationExists.location;
                }
              }
            }
            break;

          case 'conditions':
            const conditionIndex = headers.findIndex(h => h.toLowerCase().includes('condition'));
            if (conditionIndex !== -1 && row[conditionIndex]) {
              const trimmedConditionName = (row[conditionIndex] || '').trim();
              if (!trimmedConditionName) break;
              
              const normalizedConditionName = trimmedConditionName.toLowerCase();
              // Check for duplicate in CSV
              if (seenInCSV.has(normalizedConditionName)) {
                duplicateInCSV = true;
                duplicateRow = seenInCSV.get(normalizedConditionName)!;
              } else {
                seenInCSV.set(normalizedConditionName, i + 1);
                const result = await checkCondition({
                  variables: { name: trimmedConditionName },
                  fetchPolicy: 'network-only'
                });
                if (result.data?.conditions?.length > 0) {
                  isExisting = true;
                  existingRecord = result.data.conditions[0];
                }
              }
            }
            break;

          case 'customers':
            const customerNameIndex = headers.findIndex(h => h.toLowerCase().includes('name'));
            if (customerNameIndex !== -1 && row[customerNameIndex]) {
              const trimmedCustomerName = (row[customerNameIndex] || '').trim();
              if (!trimmedCustomerName) break;
              
              const normalizedCustomerName = trimmedCustomerName.toLowerCase();
              // Check for duplicate in CSV
              if (seenInCSV.has(normalizedCustomerName)) {
                duplicateInCSV = true;
                duplicateRow = seenInCSV.get(normalizedCustomerName)!;
              } else {
                seenInCSV.set(normalizedCustomerName, i + 1);
                const result = await checkCustomer({
                  variables: { name: trimmedCustomerName },
                  fetchPolicy: 'network-only'
                });
                if (result.data?.customers?.length > 0) {
                  isExisting = true;
                  existingRecord = result.data.customers[0];
                }
              }
            }
            break;

          case 'appointment-details':
            const appointmentNameIndex = headers.findIndex(h => h.toLowerCase().includes('name'));
            const appointmentCustomerIndex = headers.findIndex(h => h.toLowerCase().includes('customer'));
            if (appointmentNameIndex !== -1 && row[appointmentNameIndex]) {
              const trimmedName = (row[appointmentNameIndex] || '').trim();
              if (!trimmedName) break;
              
              const trimmedCustomerName = appointmentCustomerIndex !== -1 
                ? (row[appointmentCustomerIndex] || '').trim() || null 
                : null;
              const appointmentDetailKey = `${trimmedName.toLowerCase()}|${trimmedCustomerName ? trimmedCustomerName.toLowerCase() : 'null'}`;
              // Check for duplicate in CSV
              if (seenInCSV.has(appointmentDetailKey)) {
                duplicateInCSV = true;
                duplicateRow = seenInCSV.get(appointmentDetailKey)!;
              } else {
                seenInCSV.set(appointmentDetailKey, i + 1);
                const result = await checkAppointmentDetail({ 
                variables: { 
                    name: trimmedName, 
                    customerName: trimmedCustomerName || undefined
                  },
                  fetchPolicy: 'network-only'
                });
                if (result.data?.checkAppointmentDetailExists?.exists) {
                  isExisting = true;
                  existingRecord = result.data.checkAppointmentDetailExists.appointmentDetail;
                }
              }
            }
            break;

          case 'providers':
            const firstNameIndex = headers.findIndex(h => h.toLowerCase().includes('first') && h.toLowerCase().includes('name'));
            const lastNameIndex = headers.findIndex(h => h.toLowerCase().includes('last') && h.toLowerCase().includes('name'));
            const providerCustomerIndex = headers.findIndex(h => h.toLowerCase().includes('customer'));
            
            if (firstNameIndex === -1 || lastNameIndex === -1 || providerCustomerIndex === -1) {
              break;
            }

            const rawFirstName = row[firstNameIndex] || '';
            const rawLastName = row[lastNameIndex] || '';
            const rawCustomerName = row[providerCustomerIndex] || '';

            if (!rawFirstName || !rawLastName || !rawCustomerName) {
              break;
            }

            const trimmedFirstName = rawFirstName.trim();
            const trimmedLastName = rawLastName.trim();
            const trimmedCustomerName = rawCustomerName.trim();
            
            if (!trimmedFirstName || !trimmedLastName || !trimmedCustomerName) {
              break;
            }

            const providerKey = `${trimmedFirstName.toLowerCase()}|${trimmedLastName.toLowerCase()}|${trimmedCustomerName.toLowerCase()}`;
            
            // Check for duplicate in CSV first
            if (seenInCSV.has(providerKey)) {
              duplicateInCSV = true;
              duplicateRow = seenInCSV.get(providerKey)!;
            } else {
              // Mark as seen
              seenInCSV.set(providerKey, i + 1);
              
              // Check database
              try {
                const result = await checkProvider({ 
                variables: { 
                    firstName: trimmedFirstName, 
                    lastName: trimmedLastName,
                    customerName: trimmedCustomerName
                  },
                  fetchPolicy: 'network-only'
                });
                if (result.data?.checkProviderExists?.exists === true) {
                  isExisting = true;
                  existingRecord = result.data.checkProviderExists.provider;
                }
              } catch {
                // Continue - don't mark as existing if there's an error
              }
            }
            break;
        }
      } catch {
        // Continue with import even if check fails
      }

      previewRows.push({
        data: row,
        isExisting: isExisting || duplicateInCSV,
        existingRecord: duplicateInCSV ? { duplicateRow, isDuplicateInCSV: true } : existingRecord
      });
    }

    return previewRows;
  };

  const validateCSVHeaders = (headers: string[]): string | null => {
    const requiredColumns = getRequiredColumns();
    const missingColumns = requiredColumns.filter(required => 
      !headers.some(header => header.toLowerCase().includes(required.toLowerCase()))
    );
    
    if (missingColumns.length > 0) {
      return `Missing required columns: ${missingColumns.join(', ')}`;
    }
    
    return null;
  };

  const getRequiredColumns = (): string[] => {
    switch (importType) {
      case 'insurances':
        return ['name'];
      case 'locations':
        return ['city', 'state'];
      case 'conditions':
        return ['condition'];
      case 'customers':
        return ['name'];
      case 'appointment-details':
        return ['name'];
      case 'providers':
        return ['firstname', 'lastname', 'customer'];
      default:
        return [];
    }
  };

  const parseAndPreviewData = async () => {
    try {
      setLoading(true);
      setError(null);

      const rows = parseCSV(csvData);
      if (rows.length < 2) {
        setError('CSV must have at least a header row and one data row');
        return;
      }

      const csvHeaders = rows[0];
      if (!csvHeaders) {
        setError('Invalid CSV format: missing headers');
        return;
      }
      
      // Validate CSV headers
      const validationError = validateCSVHeaders(csvHeaders);
      if (validationError) {
        setError(validationError);
        return;
      }

      setHeaders(csvHeaders);

      const previewRows = await checkExistingRecords(rows, csvHeaders);
      setPreviewData(previewRows);
    } catch (err) {
      console.error('Error parsing CSV:', err);
      setError('Failed to parse CSV data');
    } finally {
      setLoading(false);
    }
  };

  const getImportTypeInfo = () => {
    switch (importType) {
      case 'insurances':
        return { title: 'Insurance Import Preview', icon: FileText };
      case 'locations':
        return { title: 'Location Import Preview', icon: Database };
      case 'conditions':
        return { title: 'Condition Import Preview', icon: FileText };
      case 'appointment-details':
        return { title: 'Appointment Details Import Preview', icon: FileText };
      case 'providers':
        return { title: 'Provider Import Preview', icon: Database };
      default:
        return { title: 'Import Preview', icon: FileText };
    }
  };

  const getStats = () => {
    const total = previewData.length;
    const existing = previewData.filter(row => row.isExisting).length;
    const newRecords = total - existing;
    
    return { total, existing, newRecords };
  };

  const stats = getStats();
  const info = getImportTypeInfo();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="rounded-xl shadow-xl max-w-[95vw] w-full mx-4 max-h-[95vh] flex flex-col"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-xl)'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center">
            <info.icon className="w-7 h-7 mr-3" style={{ color: 'var(--color-accent)' }} />
            <h3 className="text-xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              {info.title}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg transition-colors"
            style={{ color: 'var(--color-text-muted)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center animate-spin" style={{ backgroundColor: 'var(--color-accent-bg)' }}>
                  <Database className="w-8 h-8" style={{ color: 'var(--color-accent)' }} />
                </div>
                <p style={{ color: 'var(--color-text-secondary)' }}>Checking existing records...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="text-center max-w-md">
                <AlertTriangle className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--color-error)' }} />
                <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
                  CSV Validation Error
                </h3>
                <p className="mb-4" style={{ color: 'var(--color-text-secondary)' }}>{error}</p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4" style={{ backgroundColor: 'var(--color-error-bg)', borderColor: 'var(--color-error)' }}>
                  <h4 className="font-medium mb-2" style={{ color: 'var(--color-error)' }}>Required Columns:</h4>
                  <ul className="text-sm space-y-1" style={{ color: 'var(--color-text-secondary)' }}>
                    {getRequiredColumns().map((column, index) => (
                      <li key={index}>• {column}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Stats */}
              <div className="p-6 border-b bg-opacity-50" style={{ 
                borderColor: 'var(--color-border)',
                backgroundColor: 'var(--color-bg-secondary)'
              }}>
                <div className="grid grid-cols-3 gap-6">
                  <div className="text-center p-4 rounded-lg" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
                    <div className="text-3xl font-bold mb-1" style={{ color: 'var(--color-text-primary)' }}>
                      {stats.total}
                    </div>
                    <div className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                      Total Records
                    </div>
                  </div>
                  <div className="text-center p-4 rounded-lg" style={{ backgroundColor: 'var(--color-success-bg)' }}>
                    <div className="text-3xl font-bold mb-1" style={{ color: 'var(--color-success)' }}>
                      {stats.newRecords}
                    </div>
                    <div className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                      New Records
                    </div>
                  </div>
                  <div className="text-center p-4 rounded-lg" style={{ backgroundColor: 'var(--color-error-bg)' }}>
                    <div className="text-3xl font-bold mb-1" style={{ color: 'var(--color-error)' }}>
                      {stats.existing}
                    </div>
                    <div className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                      Existing Records
                    </div>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="flex-1 p-6" style={{ minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div className="flex-1 overflow-auto custom-scrollbar" style={{ 
                  overflowX: 'scroll',
                  overflowY: 'auto',
                  minHeight: 0
                }}>
                  <table className="w-full border-collapse" style={{ minWidth: '100%', tableLayout: 'auto' }}>
                    <thead>
                      <tr style={{ 
                        borderBottom: '2px solid var(--color-border)',
                        backgroundColor: 'var(--color-bg-secondary)',
                        position: 'sticky',
                        top: 0,
                        zIndex: 20
                      }}>
                        <th className="text-left py-2 px-3 font-semibold text-sm sticky left-0 z-30 shadow-lg" style={{ 
                          color: 'var(--color-text-primary)',
                          backgroundColor: 'var(--color-bg-secondary)',
                          minWidth: '180px',
                          maxWidth: '220px',
                          height: '40px'
                        }}>
                          Status
                        </th>
                        {headers.map((header, index) => {
                          // Determine column width based on header name
                          const getColumnWidth = (headerName: string) => {
                            const lower = headerName.toLowerCase();
                            if (lower.includes('firstname') || lower.includes('lastname')) return { minWidth: '120px', maxWidth: '150px' };
                            if (lower.includes('customer')) return { minWidth: '160px', maxWidth: '200px' };
                            if (lower.includes('email')) return { minWidth: '200px', maxWidth: '250px' };
                            if (lower.includes('phone')) return { minWidth: '130px', maxWidth: '150px' };
                            if (lower.includes('website')) return { minWidth: '180px', maxWidth: '220px' };
                            if (lower.includes('specialty')) return { minWidth: '200px', maxWidth: '280px' };
                            if (lower.includes('title')) return { minWidth: '80px', maxWidth: '100px' };
                            if (lower.includes('license') || lower.includes('npi')) return { minWidth: '110px', maxWidth: '130px' };
                            return { minWidth: '120px', maxWidth: '180px' };
                          };
                          const widths = getColumnWidth(header);
                          return (
                            <th 
                              key={index} 
                              className="text-left py-2 px-3 font-semibold text-sm" 
                              style={{ 
                                color: 'var(--color-text-primary)',
                                height: '40px',
                                ...widths
                              }}
                            >
                            {header}
                          </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((row, rowIndex) => (
                        <tr 
                          key={rowIndex} 
                          className="border-b transition-colors hover:opacity-90" 
                          style={{ 
                            borderColor: 'var(--color-border)',
                            backgroundColor: row.isExisting 
                              ? 'var(--color-error-bg)' 
                              : (rowIndex % 2 === 0 ? 'var(--color-bg-primary)' : 'var(--color-bg-secondary)')
                          }}
                        >
                          <td className="py-2 px-3 sticky left-0 z-20 shadow-lg align-middle" style={{
                            backgroundColor: row.isExisting 
                              ? 'var(--color-error-bg)' 
                              : (rowIndex % 2 === 0 ? 'var(--color-bg-primary)' : 'var(--color-bg-secondary)'),
                            minWidth: '180px',
                            maxWidth: '220px',
                            verticalAlign: 'middle'
                          }}>
                            <div className="flex items-center">
                              {row.isExisting ? (
                                <>
                                  <X className="w-4 h-4 mr-2 flex-shrink-0" style={{ color: 'var(--color-error)' }} />
                                  <span className="text-xs font-medium leading-snug" style={{ color: 'var(--color-error)', lineHeight: '1.3' }}>
                                    {row.existingRecord?.isDuplicateInCSV 
                                      ? `Duplicate (row ${row.existingRecord.duplicateRow})`
                                      : 'Skipping - duplicate already exists in database'}
                                  </span>
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" style={{ color: 'var(--color-success)' }} />
                                  <span className="text-xs font-medium leading-snug" style={{ color: 'var(--color-success)', lineHeight: '1.3' }}>
                                    New
                                  </span>
                                </>
                              )}
                            </div>
                          </td>
                          {row.data.map((cell, cellIndex) => {
                            const headerName = headers[cellIndex]?.toLowerCase() || '';
                            const isEmail = headerName.includes('email');
                            const isWebsite = headerName.includes('website');
                            const isSpecialty = headerName.includes('specialty');
                            const isLongText = isEmail || isWebsite || isSpecialty;
                            
                            // Format specialty with line breaks for pipe-delimited values
                            const formatCellContent = (content: string) => {
                              if (!content) return null;
                              if (isSpecialty && content.includes('|')) {
                                return content.split('|').map((item, idx) => (
                                  <span key={idx} className="block">
                                    {item.trim()}
                                  </span>
                                ));
                              }
                              return content;
                            };

                            return (
                              <td 
                                key={cellIndex} 
                                className="py-2 px-3 text-xs align-middle" 
                                style={{ 
                                  color: 'var(--color-text-primary)',
                                  wordBreak: isLongText ? 'break-all' : 'break-word',
                                  lineHeight: '1.4',
                                  verticalAlign: 'middle'
                                }}
                              >
                                <div className={isLongText ? 'break-words' : ''} title={cell || undefined} style={{ lineHeight: '1.4' }}>
                                  {formatCellContent(cell) || <span style={{ color: 'var(--color-text-muted)' }}>-</span>}
                                </div>
                            </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded-lg transition-colors"
              style={{ 
                borderColor: 'var(--color-border)',
                color: 'var(--color-text-secondary)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {error ? 'Close' : 'Cancel'}
            </button>
            {!error && (
              <button
                onClick={onConfirm}
                disabled={loading || isImporting}
                className="px-4 py-2 text-white rounded-lg transition-colors flex items-center gap-2"
                style={{ 
                  backgroundColor: loading || isImporting ? 'var(--color-bg-secondary)' : 'var(--color-accent)',
                  color: loading || isImporting ? 'var(--color-text-muted)' : 'white'
                }}
                onMouseEnter={(e) => {
                  if (!loading && !isImporting) {
                    e.currentTarget.style.backgroundColor = 'var(--color-accent-hover)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading && !isImporting) {
                    e.currentTarget.style.backgroundColor = 'var(--color-accent)';
                  }
                }}
              >
                {isImporting ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Importing...
                  </>
                ) : (
                  'OK'
                )}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};
